<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Lista de variáveis suportadas no body_template. Hardcoded (não via i18n)
// porque vue-i18n parseia `{{paciente}}` como nested placeholder e quebra
// a compilação do template — erro "Not allowed nest placeholder" surgiu em
// 2026-05-06 quando essa string vivia em APPOINTMENTS.REMINDERS.VARIABLES_HINT.
// Os identificadores são tokens canônicos do template engine do backend, não
// texto traduzível.
const VARIABLES_HINT =
  '{{paciente}}, {{profissional}}, {{servico}}, {{data}}, {{hora}}, {{duracao}}, {{valor}}, {{empresa}}';

let templateUidCounter = 0;
function nextUid() {
  templateUidCounter += 1;
  return `tmpl-${templateUidCounter}`;
}

// L3: assign stable _uid to each template so :key is stable (not index)
const localTemplates = ref(
  (props.modelValue || []).map(tmpl => ({
    ...tmpl,
    tUid: tmpl.tUid || nextUid(),
  }))
);

// L-07: WhatsApp template dropdown is currently empty because the existing
// /whatsapp_templates endpoint requires an inbox_id and there is no
// account-wide endpoint. Reminder templates are configured at the Service
// level (account-wide), not per-inbox, so this dropdown is a placeholder
// until the backend exposes a cross-inbox template index. Avoid the API
// call entirely to keep DevTools quiet.
const whatsappTemplates = ref([]);

watch(
  () => props.modelValue,
  val => {
    localTemplates.value = (val || []).map(tmpl => ({
      ...tmpl,
      tUid: tmpl.tUid || nextUid(),
    }));
  }
);

const emitUpdate = () => {
  emit(
    'update:modelValue',
    localTemplates.value.map(tmpl => ({ ...tmpl }))
  );
};

const addTemplate = () => {
  localTemplates.value.push({
    tUid: nextUid(),
    label: '',
    days_before: 0,
    hours_before: 1,
    minutes_before: 0,
    body_template: '',
    active: true,
    whatsapp_template_id: null,
  });
  emitUpdate();
};

const removeTemplate = idx => {
  localTemplates.value.splice(idx, 1);
  emitUpdate();
};

const updateTemplate = (idx, field, value) => {
  localTemplates.value[idx] = { ...localTemplates.value[idx], [field]: value };
  emitUpdate();
};
</script>

<template>
  <div class="space-y-4">
    <div
      v-for="(template, idx) in localTemplates"
      :key="template.tUid"
      class="border border-n-weak rounded-lg p-4 space-y-3"
    >
      <!-- L-06: inline label field (replaces hardcoded "Reminder N") -->
      <div class="flex items-center justify-between gap-3">
        <input
          :value="template.label"
          type="text"
          :placeholder="t('APPOINTMENTS.REMINDERS.LABEL_PLACEHOLDER')"
          class="flex-1 text-sm font-semibold text-n-slate-12 border-0 bg-transparent focus:outline-none focus:ring-0 px-0"
          @input="updateTemplate(idx, 'label', $event.target.value)"
        />
        <div class="flex items-center gap-3 shrink-0">
          <label class="flex items-center gap-1.5 text-sm text-n-slate-11">
            <input
              type="checkbox"
              :checked="template.active"
              class="rounded border-n-strong"
              @change="updateTemplate(idx, 'active', $event.target.checked)"
            />
            {{ t('APPOINTMENTS.REMINDERS.ACTIVE') }}
          </label>
          <button
            type="button"
            data-testid="remove-template"
            class="text-n-ruby-9 hover:text-n-ruby-11 text-sm font-medium"
            @click="removeTemplate(idx)"
          >
            {{ t('APPOINTMENTS.TABLE.DELETE') }}
          </button>
        </div>
      </div>

      <div class="grid grid-cols-3 gap-3">
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('APPOINTMENTS.REMINDERS.DAYS_BEFORE') }}
          </label>
          <input
            type="number"
            min="0"
            :value="template.days_before"
            class="w-full border border-n-strong rounded px-2 py-1 text-sm"
            @input="
              updateTemplate(idx, 'days_before', Number($event.target.value))
            "
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('APPOINTMENTS.REMINDERS.HOURS_BEFORE') }}
          </label>
          <input
            type="number"
            min="0"
            :value="template.hours_before"
            class="w-full border border-n-strong rounded px-2 py-1 text-sm"
            @input="
              updateTemplate(idx, 'hours_before', Number($event.target.value))
            "
          />
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-11 mb-1">
            {{ t('APPOINTMENTS.REMINDERS.MINUTES_BEFORE') }}
          </label>
          <input
            type="number"
            min="0"
            :value="template.minutes_before"
            class="w-full border border-n-strong rounded px-2 py-1 text-sm"
            @input="
              updateTemplate(idx, 'minutes_before', Number($event.target.value))
            "
          />
        </div>
      </div>

      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ t('APPOINTMENTS.REMINDERS.BODY') }}
        </label>

        <!-- L-08: Variables hint above body, styled blue callout -->
        <div
          class="rounded-md bg-n-blue-3 border border-n-blue-6 px-3 py-2 mb-2"
        >
          <p class="text-xs text-n-blue-12">
            <strong>{{
              t('APPOINTMENTS.REMINDERS.VARIABLES_LABEL_COLON')
            }}</strong>
            <code class="font-mono text-xs ml-1">{{ VARIABLES_HINT }}</code>
          </p>
        </div>

        <textarea
          :value="template.body_template"
          rows="3"
          class="w-full border border-n-strong rounded px-2 py-1 text-sm resize-none"
          @input="updateTemplate(idx, 'body_template', $event.target.value)"
        />
      </div>

      <!-- L-07: WhatsApp template dropdown -->
      <div>
        <label class="block text-xs font-medium text-n-slate-11 mb-1">
          {{ t('APPOINTMENTS.REMINDERS.WHATSAPP_TEMPLATE') }}
        </label>
        <div class="relative">
          <select
            :value="template.whatsapp_template_id"
            class="!mb-0 w-full appearance-none !bg-none border border-n-strong rounded-lg pl-3 pr-9 py-2 text-sm bg-n-background text-n-slate-12 transition-colors hover:border-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-n-brand"
            @change="
              updateTemplate(
                idx,
                'whatsapp_template_id',
                $event.target.value ? Number($event.target.value) : null
              )
            "
          >
            <option value="">
              {{ t('APPOINTMENTS.REMINDERS.NO_TEMPLATE') }}
            </option>
            <option
              v-for="tpl in whatsappTemplates"
              :key="tpl.id"
              :value="tpl.id"
            >
              {{ tpl.name }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down absolute right-3 top-1/2 -translate-y-1/2 size-4 text-n-slate-10 pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>

    <button
      type="button"
      class="inline-flex items-center gap-1.5 text-sm text-n-brand hover:text-n-brand font-medium"
      @click="addTemplate"
    >
      {{ t('APPOINTMENTS.REMINDERS.ADD') }}
    </button>
  </div>
</template>
